import { useState, useRef, useEffect, useMemo } from 'react';
import {
  Server,
  Wifi,
  WifiOff,
  ExternalLink,
  Trophy,
  Clock,
  AlertTriangle,
  Shield,
  Boxes,
  Network,
  Activity,
  X,
  LayoutDashboard,
} from '@/utils/icons';
import { Link } from 'react-router-dom';
import { FleetHealthScore, type DrillMetric } from '@/components/dashboard/FleetHealthScore';
import { InfoTooltip } from '@/components/common/InfoTooltip';
import { UptimeCard } from '@/components/dashboard/UptimeCard';
import { StatusCard } from '@/components/dashboard/StatusCard';
import { UptimeTrendChart } from '@/components/dashboard/UptimeTrendChart';
import { AlertsPanel } from '@/components/dashboard/AlertsPanel';
import { PortfolioTreemap } from '@/components/dashboard/PortfolioTreemap';
import { StateChangesTable } from '@/components/dashboard/StateChangesTable';
import { RollupCard } from '@/components/dashboard/RollupCard';
import { ProblemDetailDrawer } from '@/components/dashboard/ProblemDetailDrawer';
import { NodesAtRiskCard } from '@/components/dashboard/NodesAtRiskCard';
import { NodeListDrawer } from '@/components/dashboard/NodeListDrawer';
import { PageLoader } from '@/components/common/LoadingSpinner';
import { ExportMenu } from '@/components/common/ExportMenu';
import {
  useFleetSummary,
  useUptimeTrend,
  useAlerts,
  usePortfolioMetrics,
  useRecentStateChanges,
  useServiceSummary,
  useNetworkSummary,
  useDeploymentStats,
  useMetricPeriodStats,
} from '@/hooks/useMetrics';
import { useTriage } from '@/hooks/useTriage';
import { useSecuritySummary } from '@/hooks/useSecurity';
import {
  createBrandedPdf,
  downloadPdf,
  addScaledImage,
  addOfflineNodesTable,
  loadLogoForPdf,
  exportFilename,
} from '@/utils/pdf';
import { downloadCsv, formatExportDate } from '@/utils/csv';
import { formatTimeAgo } from '@/utils/formatters';
import { parseVendor } from '@/utils/calculations';
import { useNodes } from '@/hooks/useNodes';
import type { Period, UptimeTrend } from '@/types/metrics';
import { ExportDialog, periodLabel } from '@/components/common/ExportDialog';

import { useDashboardStore } from '@/stores/useDashboardStore';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';

function formatDays(days: number) {
  if (days >= 1) return `${Math.floor(days)} days`;
  const hours = days * 24;
  if (hours >= 1) return `${Math.floor(hours)} hours`;
  return `${Math.floor(hours * 60)} min`;
}

function UptimeStatsPanel({
  uptimeStats,
}: {
  uptimeStats: {
    avgUptimeDays: number;
    longestUptimeDays: number;
    longestUptimeNodeName: string | null;
    shortestUptimeDays: number;
    shortestUptimeNodeName: string | null;
    shortestUptimeInsight: string | null;
  };
}) {
  return (
    <div className="dls-space-y-3">
      <p className="dls-text-sm dls-font-semibold dls-text-muted-foreground dls-flex dls-items-center dls-gap-1">
        Hardware
        <InfoTooltip text="Time since each node's last reboot, measured from boot time. Only resets when the hardware or VM actually restarts, regardless of network connectivity." />
      </p>
      {uptimeStats.longestUptimeNodeName && (
        <div className="dls-flex dls-items-start dls-gap-3">
          <Trophy className="dls-h-4 dls-w-4 dls-text-status-warning dls-shrink-0 dls-mt-0.5" />
          <div>
            <p className="dls-text-xs dls-text-muted-foreground">Longest Uptime</p>
            <p className="dls-text-sm dls-font-semibold">{formatDays(uptimeStats.longestUptimeDays)}</p>
            <p className="dls-text-xs dls-text-muted-foreground">{uptimeStats.longestUptimeNodeName}</p>
          </div>
        </div>
      )}
      <div className="dls-flex dls-items-start dls-gap-3">
        <Clock className="dls-h-4 dls-w-4 dls-text-muted-foreground dls-shrink-0 dls-mt-0.5" />
        <div>
          <p className="dls-text-xs dls-text-muted-foreground">Average Uptime</p>
          <p className="dls-text-lg dls-font-bold">{formatDays(uptimeStats.avgUptimeDays)}</p>
        </div>
      </div>
      {uptimeStats.shortestUptimeNodeName && (
        <div className="dls-flex dls-items-start dls-gap-3">
          <Clock className="dls-h-4 dls-w-4 dls-text-status-info dls-shrink-0 dls-mt-0.5" />
          <div>
            <p className="dls-text-xs dls-text-muted-foreground">Shortest Uptime</p>
            <p className="dls-text-sm dls-font-semibold">{formatDays(uptimeStats.shortestUptimeDays)}</p>
            <p className="dls-text-xs dls-text-muted-foreground">{uptimeStats.shortestUptimeNodeName}</p>
            {uptimeStats.shortestUptimeInsight && (
              <p className="dls-text-xs dls-text-muted-foreground/70 dls-italic dls-mt-0.5">
                {uptimeStats.shortestUptimeInsight}
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export function Dashboard() {
  const [period, setPeriod] = useState<Period>('24h');
  const [customRange, setCustomRange] = useState<{ from: Date; to: Date } | undefined>();
  const [selectedProblemNodeId, setSelectedProblemNodeId] = useState<string | null>(null);
  const [metricsExpanded, setMetricsExpanded] = useState(false);
  const [drillMetric, setDrillMetric] = useState<DrillMetric | null>(null);

  const {
    selectedProfile,
    setSelectedProfile,
    selectedVendor,
    setSelectedVendor,
    selectedVersion,
    setSelectedVersion,
    selectedPortfolio,
    setSelectedPortfolio,
  } = useDashboardStore();

  // Node List Drawer State
  const [nodeListFilter, setNodeListFilter] = useState<{
    softwareVersion?: string;
    vendor?: string;
    profile?: string;
    portfolio?: string;
  } | null>(null);
  const [nodeListTitle, setNodeListTitle] = useState('');

  // Offline Nodes Drawer State
  const [offlineDrawerOpen, setOfflineDrawerOpen] = useState(false);

  // Section refs for PDF screenshot capture
  const metricsRowRef = useRef<HTMLDivElement>(null);
  const uptimeTrendRef = useRef<HTMLDivElement>(null);
  const treemapRef = useRef<HTMLDivElement>(null);
  const rollupRef = useRef<HTMLDivElement>(null);

  // Alerts scroll ref
  const alertsRef = useRef<HTMLDivElement>(null);


  const { summary: rawSummary, loading: summaryLoading } = useFleetSummary();
  const { trend, loading: trendLoading } = useUptimeTrend(period, customRange);
  const { alerts, loading: alertsLoading } = useAlerts();
  const { portfolios: rawPortfolios, loading: portfoliosLoading } = usePortfolioMetrics();
  const { changes, loading: changesLoading } = useRecentStateChanges(50, period, customRange);
  const { summary: serviceSummary, loading: serviceSummaryLoading } = useServiceSummary();
  const { summary: networkSummary, loading: networkSummaryLoading } = useNetworkSummary();
  const { nodes, loading: nodesLoading } = useNodes({ limit: 1000 });

  // Rollup card data hooks
  const { data: triageData, loading: triageLoading } = useTriage();
  const { summary: securitySummary, loading: securityLoading } = useSecuritySummary();
  const { stats: deploymentStats, loading: deploymentLoading } = useDeploymentStats();

  const periodStats = useMetricPeriodStats(period, customRange, trend, rawSummary);

  // ── Cross-filtering: filter nodes and recompute summary stats ──
  const hasFilter = !!(selectedProfile || selectedVendor || selectedVersion || selectedPortfolio);

  const filteredNodes = useMemo(() => {
    if (!hasFilter) return nodes;
    let result = nodes;
    if (selectedProfile) result = result.filter((n) => n.profileName === selectedProfile);
    if (selectedPortfolio)
      result = result.filter((n) => (n.portfolio || 'Unassigned') === selectedPortfolio);
    if (selectedVersion) result = result.filter((n) => n.softwareVersion === selectedVersion);
    if (selectedVendor) result = result.filter((n) => parseVendor(n) === selectedVendor);
    return result;
  }, [nodes, hasFilter, selectedProfile, selectedPortfolio, selectedVersion, selectedVendor]);

  const filteredSummary = useMemo(() => {
    if (!rawSummary) return null;
    if (!hasFilter) return rawSummary;

    const totalNodes = filteredNodes.length;
    const aliveNodes = filteredNodes.filter((n) => n.currentNodeState === 'ALIVE').length;
    const unreachableNodes = totalNodes - aliveNodes;
    const uptimePercent = totalNodes > 0 ? (aliveNodes / totalNodes) * 100 : 0;

    const latestVersion = rawSummary.versionCompliance.latestVersion;
    const compliant = latestVersion
      ? filteredNodes.filter((n) => n.softwareVersion === latestVersion).length
      : 0;

    // Hardware uptime from bootTime
    const now = Date.now();
    const edgeProfiles = new Set(['Edge', 'Virtual Edge']);
    const edgeNodes = filteredNodes.filter((n) => edgeProfiles.has(n.profileName ?? ''));
    const nodesWithBootTime = edgeNodes
      .filter((n) => n.bootTime && n.lastContactedAt)
      .map((n) => ({
        name: n.name,
        uptimeDays: (now - new Date(n.bootTime!).getTime()) / (1000 * 60 * 60 * 24),
      }))
      .filter((n) => n.uptimeDays >= 0);

    let uptimeStats = rawSummary.uptimeStats;
    if (nodesWithBootTime.length > 0) {
      const avg =
        Math.round(
          (nodesWithBootTime.reduce((s, n) => s + n.uptimeDays, 0) / nodesWithBootTime.length) * 10
        ) / 10;
      const longest = nodesWithBootTime.reduce((best, n) =>
        n.uptimeDays > best.uptimeDays ? n : best
      );
      const shortest = nodesWithBootTime.reduce((best, n) =>
        n.uptimeDays < best.uptimeDays ? n : best
      );
      uptimeStats = {
        ...rawSummary.uptimeStats,
        avgUptimeDays: avg,
        longestUptimeDays: Math.round(longest.uptimeDays * 10) / 10,
        longestUptimeNodeName: longest.name,
        shortestUptimeDays: Math.round(shortest.uptimeDays * 10) / 10,
        shortestUptimeNodeName: shortest.name,
        shortestUptimeInsight: null,
      };
    } else if (filteredNodes.length === 0) {
      uptimeStats = {
        ...rawSummary.uptimeStats,
        avgUptimeDays: 0,
        longestUptimeDays: 0,
        longestUptimeNodeName: null,
        shortestUptimeDays: 0,
        shortestUptimeNodeName: null,
        shortestUptimeInsight: null,
      };
    }

    // Network uptime from nodeStateChangedAt
    const aliveEdgeNodes = edgeNodes
      .filter((n) => n.currentNodeState === 'ALIVE' && n.nodeStateChangedAt && n.lastContactedAt)
      .map((n) => ({
        name: n.name,
        uptimeDays: (now - new Date(n.nodeStateChangedAt!).getTime()) / (1000 * 60 * 60 * 24),
      }))
      .filter((n) => n.uptimeDays >= 0);

    let networkUptimeStats = rawSummary.networkUptimeStats;
    if (aliveEdgeNodes.length > 0) {
      const avg =
        Math.round(
          (aliveEdgeNodes.reduce((s, n) => s + n.uptimeDays, 0) / aliveEdgeNodes.length) * 10
        ) / 10;
      const longest = aliveEdgeNodes.reduce((best, n) =>
        n.uptimeDays > best.uptimeDays ? n : best
      );
      const shortest = aliveEdgeNodes.reduce((best, n) =>
        n.uptimeDays < best.uptimeDays ? n : best
      );
      networkUptimeStats = {
        avgUptimeDays: avg,
        longestUptimeDays: Math.round(longest.uptimeDays * 10) / 10,
        longestUptimeNodeName: longest.name,
        shortestUptimeDays: Math.round(shortest.uptimeDays * 10) / 10,
        shortestUptimeNodeName: shortest.name,
      };
    } else if (filteredNodes.length === 0) {
      networkUptimeStats = {
        avgUptimeDays: 0,
        longestUptimeDays: 0,
        longestUptimeNodeName: null,
        shortestUptimeDays: 0,
        shortestUptimeNodeName: null,
      };
    }

    // Filter unreachable node list to match active filters
    const filteredNodeIds = new Set(filteredNodes.map((n) => n.id));
    const unreachableNodeList = (rawSummary.unreachableNodeList ?? []).filter((n) =>
      filteredNodeIds.has(n.id)
    );

    return {
      ...rawSummary,
      totalNodes,
      aliveNodes,
      unreachableNodes,
      uptimePercent,
      versionCompliance: { compliant, outdated: totalNodes - compliant, latestVersion },
      uptimeStats,
      networkUptimeStats,
      unreachableNodeList,
    };
  }, [rawSummary, filteredNodes, hasFilter]);

  const filteredPortfolios = useMemo(() => {
    if (!hasFilter || !(selectedProfile || selectedVersion || selectedVendor)) return rawPortfolios;
    const portfolioMap: Record<string, { total: number; alive: number }> = {};
    filteredNodes.forEach((n) => {
      const p = n.portfolio || 'Unassigned';
      if (!portfolioMap[p]) portfolioMap[p] = { total: 0, alive: 0 };
      portfolioMap[p].total++;
      if (n.currentNodeState === 'ALIVE') portfolioMap[p].alive++;
    });
    return Object.entries(portfolioMap).map(([portfolio, counts]) => ({
      portfolio,
      totalNodes: counts.total,
      aliveNodes: counts.alive,
      unreachableNodes: counts.total - counts.alive,
      uptimePercent: counts.total > 0 ? (counts.alive / counts.total) * 100 : 0,
      longestOfflineDays: 0,
      longestOfflineNodeName: null,
    }));
  }, [rawPortfolios, filteredNodes, hasFilter, selectedProfile, selectedVersion, selectedVendor]);

  // Alias filtered data so all existing JSX references work without changes
  const summary = filteredSummary;
  const portfolios = filteredPortfolios;

  // Track trend data identity for reliable export reload detection
  const trendDataRef = useRef(trend);
  trendDataRef.current = trend;
  const trendLoadingRef = useRef(trendLoading);
  trendLoadingRef.current = trendLoading;

  /** Wait for trend data to actually change after a period switch. */
  async function waitForDataReload(prevTrendData: UptimeTrend | null) {
    return new Promise<void>((resolve) => {
      const deadline = Date.now() + 15000;
      function poll() {
        if (trendDataRef.current !== prevTrendData && !trendLoadingRef.current) {
          setTimeout(resolve, 500);
        } else if (Date.now() > deadline) {
          resolve();
        } else {
          setTimeout(poll, 100);
        }
      }
      setTimeout(poll, 150);
    });
  }

  // Initialize custom range when first selecting "custom"
  useEffect(() => {
    if (period === 'custom' && !customRange) {
      const now = new Date();
      const yesterday = new Date(now);
      yesterday.setDate(yesterday.getDate() - 1);
      setCustomRange({ from: yesterday, to: now });
    }
  }, [period, customRange]);

  const formatDateForInput = (date?: Date) => {
    if (!date) return '';
    const d = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
    return d.toISOString().slice(0, 16);
  };

  const handleDateChange = (field: 'from' | 'to', value: string) => {
    if (!value) return;
    const date = new Date(value);
    if (isNaN(date.getTime())) return;
    const current = customRange ?? { from: new Date(), to: new Date() };
    setCustomRange({ ...current, [field]: date });
  };

  // Open the node list drawer showing nodes matching all active filters.
  const openFilteredDrawer = (overrides?: {
    profile?: string;
    vendor?: string;
    version?: string;
    portfolio?: string;
  }) => {
    const profile = overrides?.profile ?? selectedProfile;
    const vendor = overrides?.vendor ?? selectedVendor;
    const version = overrides?.version ?? selectedVersion;
    const portfolio = overrides?.portfolio ?? selectedPortfolio;

    const parts: string[] = [];
    if (profile) parts.push(`Profile: ${profile}`);
    if (vendor) parts.push(`Hardware: ${vendor}`);
    if (version) parts.push(`Version: ${version}`);
    if (portfolio) parts.push(`Portfolio: ${portfolio}`);
    setNodeListTitle(parts.join(' · ') || 'Nodes');

    setNodeListFilter({
      ...(profile && { profile }),
      ...(vendor && { vendor }),
      ...(version && { softwareVersion: version }),
      ...(portfolio && { portfolio }),
    });
  };

  // Export dialog state
  const [exportDialogOpen, setExportDialogOpen] = useState(false);

  // ── Rollup card computed values ──
  const triageP1P2 = useMemo(() => {
    if (!triageData) return 0;
    return (triageData.bySeverity ?? [])
      .filter((s) => s.priority === 'P1' || s.priority === 'P2')
      .reduce((sum, s) => sum + s.count, 0);
  }, [triageData]);

  const serviceUnhealthy = serviceSummary
    ? serviceSummary.unhealthy + serviceSummary.pending + serviceSummary.terminated + serviceSummary.restarting + serviceSummary.unknown
    : 0;

  const tunnelDown = networkSummary
    ? networkSummary.tunnelDisconnectedCount
    : 0;

  const resourceAlerts = deploymentStats?.resourceAlerts;

  const handleExportPdf = async (
    exportPeriod: Period,
    exportCustomRange?: { from: Date; to: Date }
  ) => {
    try {
      if (!summary) {
        window.alert('Dashboard data not loaded yet. Please wait and try again.');
        return;
      }

      setExportDialogOpen(false);

      const logoData = await loadLogoForPdf();

      const savedPeriod = period;
      const savedCustomRange = customRange;
      const periodChanged =
        exportPeriod !== period ||
        JSON.stringify(exportCustomRange) !== JSON.stringify(customRange);

      if (periodChanged) {
        const prevTrendData = trend;
        setPeriod(exportPeriod);
        setCustomRange(exportCustomRange);
        await waitForDataReload(prevTrendData);
      } else {
        await new Promise((r) => setTimeout(r, 300));
      }

      const html2canvas = (await import('html2canvas')).default;
      const captureOpts = { scale: 3, backgroundColor: '#ffffff', logging: false };

      type Capture = { imgData: string; width: number; height: number };

      const capture = async (
        ref: React.RefObject<HTMLDivElement | null>
      ): Promise<Capture | null> => {
        if (!ref.current) return null;
        const canvas = await html2canvas(ref.current, captureOpts);
        return {
          imgData: canvas.toDataURL('image/png'),
          width: canvas.width,
          height: canvas.height,
        };
      };

      // Temporarily fix DOM elements that html2canvas renders incorrectly
      const domRestorations: (() => void)[] = [];
      if (treemapRef.current) {
        treemapRef.current.querySelectorAll<HTMLElement>('.line-clamp-2').forEach((el) => {
          const saved = { className: el.className, cssText: el.style.cssText };
          el.classList.remove('line-clamp-2');
          domRestorations.push(() => {
            el.className = saved.className;
            el.style.cssText = saved.cssText;
          });
        });
      }

      await new Promise((r) => setTimeout(r, 50));

      // Capture dashboard sections
      const metricsCapture = await capture(metricsRowRef);
      const trendCapture = await capture(uptimeTrendRef);
      const treemapCapture = await capture(treemapRef);
      const rollupCapture = await capture(rollupRef);
      const alertsCapture = await capture(alertsRef);

      domRestorations.forEach((fn) => fn());

      // Build subtitle with period + filters
      const subtitleParts: string[] = [periodLabel(exportPeriod)];
      if (exportPeriod === 'custom' && exportCustomRange) {
        subtitleParts[0] = `${exportCustomRange.from.toLocaleDateString()} – ${exportCustomRange.to.toLocaleDateString()}`;
      }
      if (selectedProfile) subtitleParts.push(`Profile: ${selectedProfile}`);
      if (selectedVendor) subtitleParts.push(`Hardware: ${selectedVendor}`);
      if (selectedVersion) subtitleParts.push(`Version: ${selectedVersion}`);
      if (selectedPortfolio) subtitleParts.push(`Portfolio: ${selectedPortfolio}`);

      const layout = createBrandedPdf({
        title: 'Health Summary',
        subtitle: subtitleParts.join(' – '),
        format: 'letter',
        logoData,
      });
      const { doc } = layout;

      // Page 1: Metrics row + Rollup Strip
      const pageHeight = doc.internal.pageSize.getHeight();
      const footerZone = 16;
      const page1Available = pageHeight - footerZone - layout.startY;
      const w = layout.width;

      const p1Sections: { capture: Capture; naturalH: number }[] = [];
      for (const c of [metricsCapture, rollupCapture]) {
        if (c) {
          p1Sections.push({ capture: c, naturalH: w * (c.height / c.width) });
        }
      }
      const totalNaturalH = p1Sections.reduce((s, sec) => s + sec.naturalH, 0);
      const totalGap = Math.max(0, page1Available - totalNaturalH);
      const gapPerSection = p1Sections.length > 1 ? totalGap / (p1Sections.length - 1) : 0;
      const gap = Math.min(gapPerSection, 12);
      const usedHeight = totalNaturalH + gap * Math.max(0, p1Sections.length - 1);
      let y = layout.startY + Math.max(0, (page1Available - usedHeight) / 2);

      for (let i = 0; i < p1Sections.length; i++) {
        const { capture: c, naturalH } = p1Sections[i];
        doc.addImage(c.imgData, 'PNG', layout.left, y, w, naturalH);
        y += naturalH + (i < p1Sections.length - 1 ? gap : 0);
      }

      // Page 2: Uptime Trend + Treemap + Alerts
      if (trendCapture) {
        y = addScaledImage(
          doc,
          trendCapture.imgData,
          y,
          trendCapture.width,
          trendCapture.height,
          layout
        );
      }

      if (treemapCapture) {
        y = addScaledImage(
          doc,
          treemapCapture.imgData,
          y,
          treemapCapture.width,
          treemapCapture.height,
          layout
        );
      }

      if (alertsCapture) {
        y = addScaledImage(
          doc,
          alertsCapture.imgData,
          y,
          alertsCapture.width,
          alertsCapture.height,
          layout
        );
      }

      void y;

      downloadPdf(doc, exportFilename('dashboard-summary', 'pdf'));

      // Restore original state
      if (periodChanged) {
        setPeriod(savedPeriod);
        setCustomRange(savedCustomRange);
      }
    } catch (err) {
      window.alert(`PDF error: ${err instanceof Error ? err.stack : String(err)}`);
    }
  };

  if (summaryLoading || trendLoading || nodesLoading) {
    return <PageLoader />;
  }

  return (
    <div className="dls-space-y-6">
      <div className="dls-flex dls-items-center dls-justify-between dls-flex-wrap dls-gap-4">
        <div>
          <h1 className="dls-page-title dls-flex dls-items-center dls-gap-2"><LayoutDashboard className="dls-h-7 dls-w-7 dls-text-neeve-primary" />Dashboard</h1>
          <p className="dls-text-muted-foreground">Health overview and monitoring</p>
        </div>
        <div className="dls-flex dls-flex-col dls-items-end dls-gap-2">
          <div className="dls-flex dls-items-center dls-gap-2 dls-flex-wrap dls-justify-end">
            {selectedProfile && (
              <div className="dls-flex dls-items-center dls-gap-1.5 dls-rounded-full dls-bg-secondary dls-text-secondary-foreground dls-px-2.5 dls-py-0.5 dls-text-xs dls-font-medium">
                <button className="hover:dls-underline" onClick={() => openFilteredDrawer()}>
                  Profile: {selectedProfile}
                </button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="dls-h-3.5 dls-w-3.5 hover:dls-bg-transparent"
                  onClick={() => setSelectedProfile(null)}
                >
                  <X className="dls-h-2.5 dls-w-2.5" />
                </Button>
              </div>
            )}
            {selectedVendor && (
              <div className="dls-flex dls-items-center dls-gap-1.5 dls-rounded-full dls-bg-secondary dls-text-secondary-foreground dls-px-2.5 dls-py-0.5 dls-text-xs dls-font-medium">
                <button className="hover:dls-underline" onClick={() => openFilteredDrawer()}>
                  Hardware: {selectedVendor}
                </button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="dls-h-3.5 dls-w-3.5 hover:dls-bg-transparent"
                  onClick={() => {
                    setSelectedVendor(null);
                    setNodeListFilter(null);
                  }}
                >
                  <X className="dls-h-2.5 dls-w-2.5" />
                </Button>
              </div>
            )}
            {selectedVersion && (
              <div className="dls-flex dls-items-center dls-gap-1.5 dls-rounded-full dls-bg-secondary dls-text-secondary-foreground dls-px-2.5 dls-py-0.5 dls-text-xs dls-font-medium">
                <button className="hover:dls-underline" onClick={() => openFilteredDrawer()}>
                  Version: {selectedVersion}
                </button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="dls-h-3.5 dls-w-3.5 hover:dls-bg-transparent"
                  onClick={() => {
                    setSelectedVersion(null);
                    setNodeListFilter(null);
                  }}
                >
                  <X className="dls-h-2.5 dls-w-2.5" />
                </Button>
              </div>
            )}
            {selectedPortfolio && (
              <div className="dls-flex dls-items-center dls-gap-1.5 dls-rounded-full dls-bg-secondary dls-text-secondary-foreground dls-px-2.5 dls-py-0.5 dls-text-xs dls-font-medium">
                <button className="hover:dls-underline" onClick={() => openFilteredDrawer()}>
                  Portfolio: {selectedPortfolio}
                </button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="dls-h-3.5 dls-w-3.5 hover:dls-bg-transparent"
                  onClick={() => setSelectedPortfolio(null)}
                >
                  <X className="dls-h-2.5 dls-w-2.5" />
                </Button>
              </div>
            )}
            {hasFilter && (
              <Button
                variant="ghost"
                size="sm"
                className="dls-h-6 dls-text-xs"
                onClick={() => {
                  setSelectedProfile(null);
                  setSelectedVendor(null);
                  setSelectedVersion(null);
                  setSelectedPortfolio(null);
                  setNodeListFilter(null);
                }}
              >
                Clear all
              </Button>
            )}
            <Tabs value={period} onValueChange={(v) => setPeriod(v as Period)}>
              <TabsList>
                <TabsTrigger value="24h">24h</TabsTrigger>
                <TabsTrigger value="7d">7d</TabsTrigger>
                <TabsTrigger value="30d">30d</TabsTrigger>
                <TabsTrigger value="90d">90d</TabsTrigger>
                <TabsTrigger value="custom">Custom</TabsTrigger>
              </TabsList>
            </Tabs>
            <ExportMenu onExportPdf={() => setExportDialogOpen(true)} label="Export Summary" />
          </div>
          {period === 'custom' && (
            <div className="dls-flex dls-items-center dls-gap-2 dls-text-sm">
              <input
                type="datetime-local"
                className="dls-border dls-rounded dls-px-2 dls-py-1"
                value={formatDateForInput(customRange?.from)}
                onChange={(e) => handleDateChange('from', e.target.value)}
              />
              <span>to</span>
              <input
                type="datetime-local"
                className="dls-border dls-rounded dls-px-2 dls-py-1"
                value={formatDateForInput(customRange?.to)}
                onChange={(e) => handleDateChange('to', e.target.value)}
              />
            </div>
          )}
        </div>
      </div>

      {/* Empty state when filters produce no results */}
      {hasFilter && filteredNodes.length === 0 && (
        <div className="dls-flex dls-flex-col dls-items-center dls-justify-center dls-py-12 dls-text-center">
          <p className="dls-text-lg dls-font-medium dls-mb-1">No nodes match the current filters</p>
          <p className="dls-text-sm dls-text-muted-foreground dls-mb-4">Try removing some filters to see more results</p>
          <Button
            variant="secondary"
            onClick={() => {
              setSelectedProfile(null);
              setSelectedVendor(null);
              setSelectedVersion(null);
              setSelectedPortfolio(null);
              setNodeListFilter(null);
            }}
          >
            Clear all filters
          </Button>
        </div>
      )}

      {/* Top metrics row */}
      <div ref={metricsRowRef} className="dls-grid dls-gap-4 md:dls-grid-cols-2 lg:dls-grid-cols-5">
        {summary && (
          <>
            <FleetHealthScore
              score={summary.fleetHealthScore}
              summary={summary}
              expanded={metricsExpanded}
              onToggleExpand={() => setMetricsExpanded(!metricsExpanded)}
              drillMetric={drillMetric}
              onDrillMetricChange={setDrillMetric}
              periodStats={periodStats.healthScore}
              periodStatsLoading={periodStats.loading}
            />
            <UptimeCard
              uptime={summary.uptimePercent}
              networkUptimeStats={summary.networkUptimeStats}
              expanded={metricsExpanded}
              onToggleExpand={() => setMetricsExpanded(!metricsExpanded)}
              periodStats={periodStats.uptime}
              periodStatsLoading={periodStats.loading}
            />
            <StatusCard
              title="Total Nodes"
              value={summary.totalNodes}
              icon={<Server className="dls-h-4 dls-w-4" />}
              tooltip="Total number of Nodes being monitored across all portfolios and profiles. Click to see uptime breakdown."
              expanded={metricsExpanded}
              onToggleExpand={() => setMetricsExpanded(!metricsExpanded)}
              periodStats={periodStats.totalNodes}
              periodStatsLoading={periodStats.loading}
            >
              <UptimeStatsPanel uptimeStats={summary.uptimeStats} />
            </StatusCard>
            <StatusCard
              title="Online"
              value={summary.aliveNodes}
              variant="success"
              icon={<Wifi className="dls-h-4 dls-w-4" />}
              tooltip="Nodes currently reporting an ALIVE state and responding to health checks."
              periodStats={periodStats.online}
              periodStatsLoading={periodStats.loading}
            />
            <StatusCard
              title="Offline"
              value={summary.unreachableNodes}
              variant={summary.unreachableNodes > 0 ? 'danger' : 'default'}
              icon={<WifiOff className="dls-h-4 dls-w-4" />}
              tooltip="Nodes currently in UNREACHABLE state — not responding to health checks."
              expanded={metricsExpanded}
              onToggleExpand={() => setMetricsExpanded(!metricsExpanded)}
              onShowAll={
                summary.unreachableNodes > 0 ? () => setOfflineDrawerOpen(true) : undefined
              }
              periodStats={periodStats.offline}
              periodStatsLoading={periodStats.loading}
              invertDelta
            >
              <div className="dls-space-y-2">
                {(summary.unreachableNodeList ?? []).map((node) => (
                  <div
                    key={node.id}
                    className="dls-flex dls-items-center dls-justify-between dls-p-2 dls-rounded-md dls-bg-muted/50"
                  >
                    <div className="dls-space-y-0.5">
                      <Link
                        to={`/nodes/${node.id}`}
                        className="dls-text-sm dls-font-medium hover:dls-underline dls-flex dls-items-center dls-gap-1"
                      >
                        {node.name}
                        <ExternalLink className="dls-h-3 dls-w-3 dls-text-muted-foreground" />
                      </Link>
                      <p className="dls-text-[11px] dls-text-muted-foreground">
                        {node.state}
                        {node.portfolio ? ` · ${node.portfolio}` : ''}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </StatusCard>
          </>
        )}
      </div>

      {/* Domain Rollup Strip */}
      <div ref={rollupRef} className="dls-grid dls-gap-4 dls-grid-cols-2 lg:dls-grid-cols-5">
        <RollupCard
          icon={AlertTriangle}
          title="Triage"
          linkTo="/triage"
          primary={triageP1P2 > 0 ? `${triageP1P2} critical issues` : 'No critical issues'}
          secondary={triageData ? `${triageData.total} total in queue` : undefined}
          variant={triageP1P2 > 0 ? 'danger' : triageData && triageData.total > 0 ? 'warning' : 'good'}
          loading={triageLoading}
        />
        <RollupCard
          icon={Shield}
          title="Security"
          linkTo="/security"
          primary={securitySummary ? `Score: ${Math.round(securitySummary.securityScore)}/100` : 'Loading...'}
          secondary={securitySummary ? `${Math.round(securitySummary.twoFactorAdoptionPercent)}% 2FA adoption` : undefined}
          variant={
            securitySummary
              ? securitySummary.securityScore >= 80
                ? 'good'
                : securitySummary.securityScore >= 50
                  ? 'warning'
                  : 'danger'
              : 'default'
          }
          loading={securityLoading}
        />
        <RollupCard
          icon={Boxes}
          title="Services"
          linkTo="/services"
          primary={serviceSummary ? `${serviceSummary.healthy}/${serviceSummary.total} healthy` : 'Loading...'}
          secondary={serviceUnhealthy > 0 ? `${serviceUnhealthy} need attention` : 'All healthy'}
          variant={serviceUnhealthy > 0 ? 'warning' : 'good'}
          loading={serviceSummaryLoading}
        />
        <RollupCard
          icon={Network}
          title="Networks"
          linkTo="/networks"
          primary={networkSummary ? `${networkSummary.total} networks` : 'Loading...'}
          secondary={tunnelDown > 0 ? `${tunnelDown} tunnels down` : 'All connected'}
          variant={tunnelDown > 0 ? 'warning' : 'good'}
          loading={networkSummaryLoading}
        />
        <RollupCard
          icon={Activity}
          title="Resources"
          linkTo="/nodes"
          primary={
            resourceAlerts
              ? resourceAlerts.totalFlagged > 0
                ? `${resourceAlerts.totalFlagged} flagged`
                : 'All clear'
              : 'Loading...'
          }
          secondary={
            resourceAlerts
              ? resourceAlerts.totalFlagged > 0
                ? `${resourceAlerts.criticalCount} critical / ${resourceAlerts.cautionCount} caution`
                : 'All within thresholds'
              : undefined
          }
          variant={
            resourceAlerts
              ? resourceAlerts.criticalCount > 0
                ? 'danger'
                : resourceAlerts.cautionCount > 0
                  ? 'warning'
                  : 'good'
              : 'default'
          }
          loading={deploymentLoading}
        />
      </div>

      {/* Uptime Trend + Portfolio Treemap — side by side */}
      <div className="dls-grid dls-gap-6 lg:dls-grid-cols-2">
        {trend && (
          <div ref={uptimeTrendRef}>
            <UptimeTrendChart
              data={trend.dataPoints}
              period={period}
              averageUptime={trend.averageUptime}
              dataQualityEpoch={trend.dataQualityEpoch}
            />
          </div>
        )}
        {!portfoliosLoading && portfolios.length > 0 && (
          <div ref={treemapRef}>
            <PortfolioTreemap
              portfolios={portfolios}
              selectedPortfolio={selectedPortfolio}
              onSelect={setSelectedPortfolio}
            />
          </div>
        )}
      </div>

      {/* Alerts + Nodes at Risk — fixed height matching Uptime Trend card */}
      <div className="dls-grid dls-gap-6 lg:dls-grid-cols-2">
        {!alertsLoading && (
          <div ref={alertsRef} className="dls-h-[420px]" style={{ scrollMarginTop: '5rem' }}>
            <AlertsPanel
              alerts={alerts}
              onAlertClick={(nodeId) => setSelectedProblemNodeId(nodeId)}
            />
          </div>
        )}
        <div className="dls-h-[420px]">
          <NodesAtRiskCard />
        </div>
      </div>

      {/* Recent state changes */}
      {!changesLoading && <StateChangesTable events={changes} />}

      {/* Problem Detail Drawer */}
      <ProblemDetailDrawer
        nodeId={selectedProblemNodeId}
        onClose={() => setSelectedProblemNodeId(null)}
      />

      {/* Node List Drawer */}
      <NodeListDrawer
        isOpen={!!nodeListFilter}
        onClose={() => setNodeListFilter(null)}
        title={nodeListTitle}
        filter={nodeListFilter}
      />

      {/* Offline Nodes Drawer */}
      {offlineDrawerOpen && summary && (
        <>
          <div
            className="dls-fixed dls-inset-0 dls-bg-black/40 dls-z-40"
            onClick={() => setOfflineDrawerOpen(false)}
          />
          <div className="dls-fixed dls-right-0 dls-top-0 dls-bottom-0 dls-w-full dls-max-w-2xl dls-bg-background dls-border-l dls-shadow-50 dls-z-50 dls-flex dls-flex-col">
            <div className="dls-flex dls-items-center dls-justify-between dls-px-6 dls-py-4 dls-border-b">
              <div>
                <h2 className="dls-text-lg dls-font-semibold">Offline Nodes</h2>
                <p className="dls-text-sm dls-text-muted-foreground">
                  {summary.unreachableNodeList?.length ?? 0} Nodes not responding to health checks
                </p>
              </div>
              <div className="dls-flex dls-items-center dls-gap-2">
                {(summary.unreachableNodeList?.length ?? 0) > 0 && (
                  <ExportMenu
                    onExportPdf={async () => {
                      const offlineNodes = summary.unreachableNodeList ?? [];
                      if (offlineNodes.length === 0) return;
                      const logoData = await loadLogoForPdf();
                      const layout = createBrandedPdf({
                        title: 'Offline Nodes',
                        subtitle: `${offlineNodes.length} Nodes not responding`,
                        logoData,
                      });
                      addOfflineNodesTable(layout.doc, offlineNodes, layout.startY, layout);
                      downloadPdf(layout.doc, exportFilename('offline-nodes', 'pdf'));
                    }}
                    onExportCsv={() => {
                      const offlineNodes = summary.unreachableNodeList ?? [];
                      if (offlineNodes.length === 0) return;
                      downloadCsv(
                        offlineNodes.map((n) => ({
                          Name: n.name,
                          'S/N': n.serialNumber ?? '',
                          State: n.state,
                          Profile: n.profileName ?? '',
                          Version: n.softwareVersion ?? '',
                          Portfolio: n.portfolio ?? '',
                          'Last Contact': formatExportDate(n.lastContactedAt),
                        })),
                        exportFilename('offline-nodes', 'csv'),
                        { report: 'Offline Nodes' }
                      );
                    }}
                    label="Export"
                  />
                )}
                <Button variant="ghost" size="icon" onClick={() => setOfflineDrawerOpen(false)}>
                  <X className="dls-h-5 dls-w-5" />
                </Button>
              </div>
            </div>
            <div className="dls-flex-1 dls-overflow-y-auto dls-p-6">
              <div className="dls-space-y-2">
                {(summary.unreachableNodeList ?? []).map((node) => (
                  <div
                    key={node.id}
                    className="dls-flex dls-items-center dls-justify-between dls-p-3 dls-border dls-rounded-lg hover:dls-bg-muted/50 dls-transition-colors"
                  >
                    <div className="dls-space-y-1">
                      <Link
                        to={`/nodes/${node.id}`}
                        className="dls-font-medium dls-text-sm hover:dls-underline dls-flex dls-items-center dls-gap-1"
                      >
                        {node.name}
                        <ExternalLink className="dls-h-3 dls-w-3 dls-text-muted-foreground" />
                      </Link>
                      <div className="dls-grid dls-grid-cols-2 dls-gap-x-4 dls-gap-y-0.5 dls-text-xs dls-text-muted-foreground">
                        <span>S/N: {node.serialNumber || '–'}</span>
                        <span>State: {node.state}</span>
                        <span>Profile: {node.profileName || '–'}</span>
                        <span>Version: {node.softwareVersion || '–'}</span>
                        <span>Portfolio: {node.portfolio || '–'}</span>
                        <span>
                          Last Contact:{' '}
                          {node.lastContactedAt ? formatTimeAgo(node.lastContactedAt) : '–'}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </>
      )}

      {/* Export Dialog */}
      <ExportDialog
        open={exportDialogOpen}
        onOpenChange={setExportDialogOpen}
        onExport={handleExportPdf}
        activeFilters={{
          profile: selectedProfile,
          vendor: selectedVendor,
          version: selectedVersion,
          portfolio: selectedPortfolio,
        }}
      />
    </div>
  );
}
